import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    OnInit,
    ViewChild
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MaterialModule} from '../../material.module';
import {PRODUCT_SLUGS, productFromSlug, ProductSlug} from '../../shared/products';
import {HubspotFormComponent} from '../hubspot-form/hubspot-form.component';
import {ScreenshotCarouselComponent} from '../screenshot-carousel/screenshot-carousel.component';

interface ProductLandingContent {
    name: string;
    tagline: string;
    summary: string;
    highlights: string[];
    formTitle: string;
    supportingCopy: string;
    slug: ProductSlug;
    eyebrow: string;
    primaryCta: string;
    secondaryCta: string;
    formCopy: string;
    screenshots: string[];
}

const PRODUCT_CONTENT: Record<ProductSlug, ProductLandingContent> = {
    performance: {
        name: 'Shine Performance',
        tagline: 'Marketing performance across channels.',
        summary: 'Compare channel spend, clicks and impressions in one reporting workspace, with source coverage and client links for marketing and operations teams.',
        highlights: [
            'Compare channels in one reporting currency',
            'Identify missing source days and exchange rates',
            'Keep Client Sheets and Productive mappings together',
        ],
        formTitle: 'Discuss Shine Performance access',
        supportingCopy: 'Shine Performance is being verified as a separate replacement while current reports and invoicing continue to run.',
        slug: 'performance',
        eyebrow: 'Performance access required',
        primaryCta: 'Request access',
        secondaryCta: 'See the scope',
        formCopy: 'Access and reporting sources must be configured for your organization.',
        screenshots: [],
    },
    ai: {
        name: 'Shine AI',
        tagline: 'Put AI to work—without losing control.',
        summary:
            'Shine AI powers conversational assistants, function calling, and real-time streaming—so you can build AI experiences for customers and teams with governance and predictable operations.',
        highlights: [
            'Governed backend assistants (no direct UI LLM calls)',
            'Real-time streaming for responsive chat experiences',
            'Connect to approved tools and systems with safeguards',
        ],
        formTitle: 'Book a Shine AI demo',
        supportingCopy:
            'Build real-time assistants that connect to your systems, with privacy and operational guardrails that keep adoption safe and costs predictable.',
        slug: 'ai',
        eyebrow: 'No subscription active',
        primaryCta: 'Book a demo',
        secondaryCta: 'See how it works',
        formCopy: 'Want AI that fits your governance and operations model? Book a demo.',
        screenshots: [
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20AI%20-%20Chatbot.png'
        ],
    },
    translations: {
        name: 'Shine Translations',
        tagline: 'Localize HubSpot content faster with AI.',
        summary:
            'Shine Translations helps you translate site pages, landing pages, and blog posts into new languages while preserving structure, tokens, and modules.',
        highlights: [
            'Translate drafts with per-client and per-job instructions',
            'Create or reuse language variants in HubSpot automatically',
            'Module-aware policies for forms and CTAs with review feedback',
        ],
        formTitle: 'Book a Shine Translations demo',
        supportingCopy:
            'Turn HubSpot pages into localized drafts with review checks, module policies, and governance-friendly AI flows.',
        slug: 'translations',
        eyebrow: 'No subscription active',
        primaryCta: 'Book a demo',
        secondaryCta: 'See how it works',
        formCopy: 'Want to speed up content localization in HubSpot? Book a demo.',
        screenshots: [
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20AI%20-%20Chatbot.png'
        ],
    },
    connect: {
        name: 'Shine Connect',
        tagline: 'Workflows and integrations for revenue ops.',
        summary:
            'Shine Connect is a workflow control plane that helps you connect systems and automate repeatable processes—so teams can move faster with less manual work.',
        highlights: [
            'Cross-system workflows with retries, timeouts, and safeguards',
            'Reusable connector model (triggers/actions) for integrations',
            'Policy-driven handling of sensitive data for safer automation',
        ],
        formTitle: 'Book a Shine Connect demo',
        supportingCopy:
            'Automate lead routing, lifecycle updates, and campaign handoffs across your stack with reliable integrations and built-in governance.',
        slug: 'connect',
        eyebrow: 'No subscription active',
        primaryCta: 'Book a demo',
        secondaryCta: 'See how it works',
        formCopy: 'If your revenue ops rely on manual glue work, Shine Connect can help. Book a demo.',
        screenshots: [
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Connect%20Workf.png'
        ],
    },
    enrich: {
        name: 'Shine Enrich',
        tagline: 'Accurate company data in your CRM—automatically.',
        summary:
            'Shine Enrich keeps company records accurate and complete by enriching your CRM with up-to-date data from the Norwegian Business Registry (Brønnøysundregistrene).',
        highlights: [
            'Reliable matching using organization numbers as a key',
            'Real-time, scheduled, or manual refresh to prevent drift',
            'Choose exactly which fields to map into your CRM',
        ],
        formTitle: 'Book a Shine Enrich demo',
        supportingCopy:
            'Save time, improve segmentation, and give sales and marketing better context with authoritative registry data in HubSpot (CRM-agnostic).',
        slug: 'enrich',
        eyebrow: 'No subscription active',
        primaryCta: 'Book a demo',
        secondaryCta: 'See how it works',
        formCopy: 'Want to see how Shine Enrich fits your HubSpot or CRM setup? Book a demo.',
        screenshots: [
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Enrich%20Filters.png',
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Enrich%20Settings.png',
        ],
    },
    webinars: {
        name: 'Shine Webinars',
        tagline: 'Branded webinar pages, registration, and engagement.',
        summary:
            'Shine Webinars provides public webinar landing pages, attendee login, real-time chat, and secure admin tooling—so you can run professional events and turn engagement into measurable follow-up.',
        highlights: [
            'Public listing of upcoming and past webinars',
            'Lightweight attendee login with real-time chat and reactions',
            'Admin APIs for webinar management and registration exports',
        ],
        formTitle: 'Book a Shine Webinars demo',
        supportingCopy:
            'Run reliable, on-brand webinars with chat, playback, and clean exports that integrate into your revenue process.',
        slug: 'webinars',
        eyebrow: 'No subscription active',
        primaryCta: 'Book a demo',
        secondaryCta: 'See how it works',
        formCopy: 'Want webinar pages and engagement that integrate into your revenue process? Book a demo.',
        screenshots: [
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Webinars%20-%20Live%20Webinar.png',
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Webinars%20-%20Webinar%20Settings.png',
            'https://intuvio.com/hubfs/Shine/Shine%20Platform%20Screenshots/Shine%20Webinars%20-%20Webinars%20list.png'
        ],
    },
};

@Component({
    selector: 'app-product-landing',
    standalone: true,
    imports: [MaterialModule, HubspotFormComponent, ScreenshotCarouselComponent],
    templateUrl: './product-landing.component.html',
    styleUrls: ['./product-landing.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductLandingComponent implements OnInit {
    private readonly route = inject(ActivatedRoute);

    @ViewChild('formAnchor') formAnchor?: ElementRef<HTMLElement>;
    private readonly cdr = inject(ChangeDetectorRef);
    product: ProductLandingContent = PRODUCT_CONTENT.ai;

    ngOnInit(): void {
        this.route.url.subscribe((segments) => {
            const slugParam = segments[1]?.path?.toLowerCase() ?? 'ai';
            const productType = productFromSlug(slugParam);
            const resolvedSlug = PRODUCT_SLUGS[productType];
            this.product = PRODUCT_CONTENT[resolvedSlug] || PRODUCT_CONTENT.ai;
            this.cdr.markForCheck();
        });
    }


    scrollToForm(): void {
        if (!this.formAnchor?.nativeElement) {
            return;
        }

        this.formAnchor.nativeElement.scrollIntoView({behavior: 'smooth', block: 'start'});
    }
}
